import { Dispatch, SetStateAction } from 'react';
import { API_ROUTES } from 'lib/api-routes';
import { ExperienceAttributes } from 'lib/models/candidate';
import { parseArrayResponse, parseErrorResponse } from 'lib/utils/parser';
import { TCandidate } from 'lib/models/User.model';

export const generateWorkExperienceDescription: (
  candidateExperience: ExperienceAttributes & {
    isNew: boolean;
  },
  headers: Headers,
  setAbortController?: Dispatch<SetStateAction<AbortController | undefined>>,
  improvement?: boolean
) => Promise<
  | {
      suggestions: string[];
    }
  | undefined
> = async (candidateExperience, headers, setAbortController, improvement) => {
  const controller = new AbortController();
  setAbortController && setAbortController(controller);

  const { NEXT_PUBLIC_API_HOST_URL } = process.env;

  const url = improvement
    ? API_ROUTES.AI_SUGGESTIONS_WORK_EXPERIENCE_IMPROVEMENTS
    : API_ROUTES.AI_SUGGESTIONS_WORK_EXPERIENCE_FROM_SCRATCH;

  const body = {
    work_history: {
      organisation_name: candidateExperience?.organisation_name,
      role: candidateExperience?.role,
      website: candidateExperience?.website,
      start_date: candidateExperience?.start_date,
      end_date: candidateExperience?.end_date,
      presently_working: candidateExperience?.presently_working,
      location: candidateExperience?.location,
      ...(improvement && {
        description: (candidateExperience?.description as string)
          ?.split('\n')
          .map((item) => item.replace(/•/g, '').trim()),
      }),
    },
  };

  const args = {
    method: 'POST',
    body: JSON.stringify(body),
    headers,
    signal: controller.signal,
  };
  const request = new Request(`${NEXT_PUBLIC_API_HOST_URL}${url}`, args);

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response: any = await fetch(request);
    return response.json() as { suggestions: string[] };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    const err = parseErrorResponse(error);
    if (err !== 'The user aborted a request.') {
      throw new Error(error.alert || error.error);
    }
  }
};

export const getUpdateWorkHistoryValue = async (
  info: ExperienceAttributes & {
    isNew: boolean;
  }
) => {
  const {
    id,
    isNew,
    organisation_name,
    role,
    start_date,
    end_date,
    description,
    website,
    generated_description_disable,
    generated_description,
    presently_working,
    location,
  } = info;

  const candidate: { work_histories_attributes: ExperienceAttributes[] } = {
    work_histories_attributes: [
      {
        id: isNew ? '' : id,
        start_date,
        end_date,
        description: !Array.isArray(description)
          ? description
              ?.split('\n')
              .map((item) => item.replace(/•/g, '').trim())
          : description,
        organisation_name,
        role,
        website,
        generated_description_disable,
        generated_description,
        presently_working,
        location,
      },
    ],
  };

  return candidate;
};

export const getExperienceInfo = (candidate: TCandidate) => {
  const experiencesList = parseArrayResponse<ExperienceAttributes>(
    candidate.work_histories
  );
  const experiences = experiencesList.map((item) => {
    return {
      ...item,
      description: (item.description as string[])
        ?.map((pointer) => (pointer ? `• ${pointer}` : ''))
        .join('\n'),
      isNew: false,
    };
  });
  return experiences;
};
